// Точка входа React-приложения.
// 1. Импорт global.css (с tokens + шрифты).
// 2. Определение platform (TMA vs web) — ставим data-platform на <body>
//    для условных стилей в global.css.
// 3. Инициализация Telegram WebApp (ready/expand/theme).
// 4. Mount React-дерева.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import App from './App';
import { ErrorBoundary, reportGlobalCrash } from './components/ErrorBoundary';
import { getTelegramApp, isInTelegram } from './telegram/useTelegram';
import { authViaTelegram, getProfile, getToken, type AuthResponse } from './api/client';
import { useGameStore } from './store/useGameStore';
import './styles/global.css';
// ФТУЕ.0: инициализация i18next до первого рендера. Файл сам вызывает
// init() при импорте. После syncFromServer язык переключится на
// player.locale из БД (если он отличается от детектора Telegram-языка).
import './i18n';
import { setLocaleLocal } from './i18n';

// Манифест TON Connect — Vite раздаёт public/tonconnect-manifest.json
// по URL /tonconnect-manifest.json. В production URL станет настоящим
// доменом — пока локальный.
const TONCONNECT_MANIFEST_URL = `${window.location.origin}/tonconnect-manifest.json`;

const platform: 'tma' | 'web' = isInTelegram() ? 'tma' : 'web';
document.body.dataset.platform = platform;

// Telegram Desktop НЕ имеет DevTools (Ctrl+Shift+I не работает), без
// on-screen overlay'я клиентские крахи диагностируются вслепую.
// Ловим необработанные exceptions и promise-rejections в глобальный
// reportGlobalCrash → ErrorBoundary рисует красный экран со stack
// trace + кнопкой «Скопировать».
window.addEventListener('error', (event) => {
  reportGlobalCrash({
    message: event.message || String(event.error || 'unknown error'),
    stack: event.error?.stack,
    source: 'window.onerror',
  });
});
window.addEventListener('unhandledrejection', (event) => {
  const reason: any = event.reason;
  reportGlobalCrash({
    message: reason?.message || String(reason || 'unhandled promise rejection'),
    stack: reason?.stack,
    source: 'unhandledrejection',
  });
});

// В TMA сразу зовём ready() и expand() — стандартный паттерн Mini App SDK.
// Затем применяем Telegram-тему (themeParams) к нашим CSS-переменным.
// Если Telegram прислал колорсхему — синхронизируем colorScheme на :root.
if (platform === 'tma') {
  const tg = getTelegramApp() as any;
  if (tg) {
    try {
      tg.ready();
      tg.expand();
      // Fullscreen только на мобильных. На десктопе (tdesktop, macos, weba,
      // webk, unigram) оставляем в режиме окна — юзер может тащить за края
      // окна Telegram и менять размер. На телефоне fullscreen скрывает
      // верхнюю шапку Telegram и даёт всю высоту экрана.
      // В @BotFather Launch Mode рекомендуется поставить Compact или
      // Fullsize — fullscreen на мобиле добивается отсюда.
      const platform = String(tg.platform || '').toLowerCase();
      const isMobile = platform === 'android' || platform === 'android_x' || platform === 'ios';
      if (isMobile) {
        try { tg.requestFullscreen?.(); } catch {}
      }
      // Telegram цветовая схема (light/dark) проставляется на body.
      document.documentElement.dataset.colorScheme = tg.colorScheme;

      // Safe-area: Telegram передаёт contentSafeAreaInset (от шапки
      // приложения) и safeAreaInset (от системного status bar). В
      // Fullscreen режиме contentSafeAreaInset обычно меньше — шапки
      // нет, но статус-бар ОС остаётся. Берём максимум.
      const applySafeArea = () => {
        const csa = tg.contentSafeAreaInset || {};
        const sa = tg.safeAreaInset || {};
        const top = Math.max(csa.top || 0, sa.top || 0);
        document.documentElement.style.setProperty('--tg-safe-top', `${top}px`);
        const bottom = Math.max(csa.bottom || 0, sa.bottom || 0);
        document.documentElement.style.setProperty('--tg-safe-bottom', `${bottom}px`);
      };
      applySafeArea();
      tg.onEvent?.('safeAreaChanged', applySafeArea);
      tg.onEvent?.('contentSafeAreaChanged', applySafeArea);
      tg.onEvent?.('viewportChanged', applySafeArea);
    } catch (e) {
      console.warn('[Корпы] Telegram.WebApp init warning:', e);
    }
  }
}

console.log(`%c[Корпы] React-клиент v0.1 (platform=${platform})`,
  'color:#3DF0FF;font-weight:bold');

// РЕФ.1: запоминает что нужно показать онбординг-модалку «Введи Сигнал»
// при первом входе нового игрока без реферера. Флаг живёт в localStorage,
// чтобы пережить F5 в течение этой сессии. Удаляется когда юзер пропустит
// или введёт код.
function rememberOnboarding(auth: AuthResponse) {
  if (auth.isNewPlayer && !auth.hasReferrer) {
    try { localStorage.setItem('corps.needsReferrerOnboarding', '1'); } catch {}
  }
}

// Ключ для запомненного telegram_id текущей сессии. При смене ТГ-аккаунта
// на устройстве (в Telegram-приложении 2-3 аккаунта) initDataUnsafe.user.id
// изменится — мы это поймём и переавторизуемся под новым.
const LS_TG_ID_KEY = 'corps.tgId';

function getCurrentTgId(): number | null {
  const tg = getTelegramApp() as any;
  const id = tg?.initDataUnsafe?.user?.id;
  return typeof id === 'number' ? id : null;
}

function getSavedTgId(): number | null {
  try {
    const raw = localStorage.getItem(LS_TG_ID_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

function saveTgId(id: number | null) {
  try {
    if (id != null) localStorage.setItem(LS_TG_ID_KEY, String(id));
    else localStorage.removeItem(LS_TG_ID_KEY);
  } catch { /* ignore */ }
}

// Async-инициализация сессии. Не блокирует рендер UI — компоненты сначала
// показывают mock-данные из useGameStore, после успешного syncFromServer
// перерисовываются с реальными значениями.
async function initSession() {
  try {
    // Проверка смены ТГ-аккаунта: если в Telegram сейчас другой user.id чем
    // был запомнен → выбрасываем старый JWT (он от прошлого аккаунта) и
    // авторизуемся заново.
    if (platform === 'tma') {
      const currentTgId = getCurrentTgId();
      const savedTgId = getSavedTgId();
      if (currentTgId != null && savedTgId != null && currentTgId !== savedTgId) {
        console.log('[session] tg account switched:', savedTgId, '→', currentTgId, '— re-auth');
        const { clearToken } = await import('./api/client');
        clearToken();
        saveTgId(null);
      }
    }

    // 1. Auth: если уже есть JWT в localStorage — пропускаем; иначе шлём initData.
    if (!getToken()) {
      const tg = getTelegramApp();
      const initData = tg?.initData || '';
      // РЕФ.1: реф-Сигнал из start_param (Telegram передаёт его при
      // открытии Mini App через t.me/cyvercorpsbot/app?startapp=КОД).
      const ref = (tg?.initDataUnsafe?.start_param as string | undefined)
        || (new URLSearchParams(window.location.search).get('startapp') ?? undefined)
        || undefined;
      if (!initData && platform === 'tma') {
        console.warn('[session] empty initData in TMA — пропускаем auth');
        return;
      }
      if (!initData) {
        // web-режим без Telegram. Делаем мок-auth для теста (id=0).
        // В production сервер должен это запретить.
        const fakeUser = encodeURIComponent(JSON.stringify({ id: 12345, first_name: 'WebDev' }));
        const auth = await authViaTelegram(
          `user=${fakeUser}&auth_date=${Math.floor(Date.now()/1000)}`,
          ref
        );
        console.log('[session] dev auth ok, player', auth.player.id, 'ref=', ref);
        rememberOnboarding(auth);
      } else {
        const auth = await authViaTelegram(initData, ref);
        console.log('[session] tma auth ok, player', auth.player.id, 'ref=', ref);
        rememberOnboarding(auth);
        // Запоминаем telegram_id текущей сессии — для детекции смены
        // ТГ-аккаунта при следующих заходах.
        const tgId = getCurrentTgId();
        if (tgId != null) saveTgId(tgId);
      }
    }
    // 2. Профиль: подтягиваем полный snapshot.
    const profile = await tryGetProfile();
    if (profile) {
      useGameStore.getState().syncFromServer(profile);
      // ФТУЕ.0: подхватываем серверный язык (может отличаться от
      // детектора Telegram при первом входе).
      if (profile.player.locale === 'en' || profile.player.locale === 'ru') {
        setLocaleLocal(profile.player.locale);
      }
      console.log('[session] profile loaded');
    }
  } catch (e) {
    console.warn('[session] init failed (offline mode):', e);
  }
}

// Загрузка профиля с авто-перелогином при 401/404. Если токен невалиден
// (другой DB, истёк JWT_SECRET, удалили игрока) — чистим токен и пробуем
// заново.
async function tryGetProfile() {
  try {
    return await getProfile();
  } catch (e: any) {
    if (e?.status === 401 || e?.status === 404) {
      console.warn('[session] токен невалиден или игрок не найден — перелогин');
      const { clearToken } = await import('./api/client');
      clearToken();
      saveTgId(null);
      // Повторная авто-авторизация в фоне (без await — тихо в catch).
      const tg = getTelegramApp();
      const initData = tg?.initData || '';
      if (initData || platform === 'web') {
        const data = initData || `user=${encodeURIComponent(JSON.stringify({ id: 12345, first_name: 'WebDev' }))}&auth_date=${Math.floor(Date.now()/1000)}`;
        await authViaTelegram(data);
        // Запоминаем новый telegram_id если он есть.
        const tgId = getCurrentTgId();
        if (tgId != null) saveTgId(tgId);
        return await getProfile();
      }
    }
    throw e;
  }
}

initSession();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found in index.html');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ErrorBoundary>
      <TonConnectUIProvider manifestUrl={TONCONNECT_MANIFEST_URL}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TonConnectUIProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
